import React, { useMemo, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const WD = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const ART_CLS = {
  frist: "bg-[#D32F2F] text-white",
  vorfrist: "bg-[#F59E0B]/90 text-white",
  wiedervorlage: "bg-[#0EA5E9] text-white",
};
export const ART_LABEL = { frist: "Frist", vorfrist: "Vorfrist", wiedervorlage: "Wiedervorlage" };

const iso = (d) => d.toISOString().slice(0, 10);
const toLocalISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Monatskalender: Fristen (rot), Vorfristen (gelb), Wiedervorlagen (blau)
export default function FristenKalender({ events, monat, onMonat, selected, onSelect }) {
  const [hover, setHover] = useState(null);
  const y = monat.getFullYear(), m = monat.getMonth();
  const first = new Date(y, m, 1);
  const startOffset = (first.getDay() + 6) % 7; // Montag = 0
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const today = toLocalISO(new Date());

  const byDay = useMemo(() => {
    const map = {};
    (events || []).forEach(e => { (map[e.datum] = map[e.datum] || []).push(e); });
    return map;
  }, [events]);

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid="fristen-kalender">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB]">
        <button className="h-8 w-8 rounded hover:bg-[#F3F4F6] flex items-center justify-center" onClick={() => onMonat(new Date(y, m - 1, 1))} data-testid="kal-prev"><CaretLeft size={16} /></button>
        <div className="text-center">
          <p className="font-serif text-xl" data-testid="kal-monat">{monat.toLocaleDateString("de-DE", { month: "long", year: "numeric" })}</p>
          <button className="text-[11px] text-[#1A2238] hover:underline" onClick={() => { const n = new Date(); onMonat(new Date(n.getFullYear(), n.getMonth(), 1)); onSelect(today); }} data-testid="kal-heute">Heute</button>
        </div>
        <button className="h-8 w-8 rounded hover:bg-[#F3F4F6] flex items-center justify-center" onClick={() => onMonat(new Date(y, m + 1, 1))} data-testid="kal-next"><CaretRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 text-[11px] uppercase tracking-wide text-[#9CA3AF] border-b border-[#E5E7EB]">
        {WD.map(w => <div key={w} className="px-2 py-1.5 text-center">{w}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} className="min-h-[72px] sm:min-h-[92px] border-b border-r border-[#F3F4F6] bg-[#FAFAFA]" />;
          const key = toLocalISO(d);
          const evs = byDay[key] || [];
          const isToday = key === today;
          const isSel = key === selected;
          const weekend = d.getDay() === 0 || d.getDay() === 6;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelect(key)}
              onMouseEnter={() => setHover(key)}
              onMouseLeave={() => setHover(null)}
              className={`min-h-[72px] sm:min-h-[92px] border-b border-r border-[#F3F4F6] p-1.5 text-left align-top transition-colors ${isSel ? "bg-[#1A2238]/5 ring-1 ring-inset ring-[#1A2238]" : hover === key ? "bg-[#F9FAFB]" : weekend ? "bg-[#FCFCFC]" : "bg-white"}`}
              data-testid={`kal-tag-${key}`}
            >
              <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-mono ${isToday ? "bg-[#D32F2F] text-white font-bold" : "text-[#4B5563]"}`}>{d.getDate()}</span>
              <div className="mt-1 space-y-0.5">
                {evs.slice(0, 3).map(e => (
                  <div key={`${e.art}-${e.id}`} className={`truncate rounded px-1 py-0.5 text-[10px] leading-tight ${e.erledigt ? "bg-[#E5E7EB] text-[#9CA3AF] line-through" : ART_CLS[e.art]}`} title={e.titel} data-testid={`kal-event-${e.art}`}>
                    {e.titel}
                  </div>
                ))}
                {evs.length > 3 && <div className="text-[10px] text-[#6B7280] px-1">+{evs.length - 3} weitere</div>}
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 px-4 py-2 text-[11px] text-[#6B7280] border-t border-[#E5E7EB]">
        <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-[#D32F2F]" /> Frist</span>
        <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-[#F59E0B]" /> Vorfrist</span>
        <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-[#0EA5E9]" /> Wiedervorlage</span>
        <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-[#E5E7EB]" /> erledigt</span>
      </div>
    </div>
  );
}

export { iso, toLocalISO };
